/* ──────────────────────────────────────────────────────────────
   Statistics with two levels (static/js/analytics.js):

   1. anonymous — runs for every visitor without asking: nothing is
      stored on the device, no visitor id, no campaign recipient (cid).
      Legal basis: legitimate interest; visitors can object in the
      privacy policy ("Don't count my visits", OPTOUT_KEY).
   2. full — only after "Allow" in the ConsentBanner: a visitor cookie
      for two years, the session in sessionStorage, the cid link.

   The choice is kept in localStorage under KEY ('anonymous' |
   'granted'). That, like the opt-out flag, is strictly necessary
   storage and needs no consent of its own.
─────────────────────────────────────────────────────────────── */

const KEY = 'rb-consent';
const OPTOUT_KEY = 'rb-analytics-optout';
const OPEN_EVENT = 'rb-consent-open';
const CHANGE_EVENT = 'rb-consent-change';

/* the site-wide statistics stream */
const SCRIPT = '/js/analytics.js?v=20261029g';
const SITE_STREAM = '_rbu30bbd21215654cc1a4d3';

const isBrowser = typeof window !== 'undefined';

const read = (key) => {
  try {
    return window.localStorage.getItem(key);
  } catch {
    return null;
  }
};

const write = (key, value) => {
  try {
    if (value === null) window.localStorage.removeItem(key);
    else window.localStorage.setItem(key, value);
  } catch {
    /* storage blocked — the choice holds for this page view only */
  }
};

/* 'granted' | 'anonymous' | null (not asked yet) */
export const getConsent = () => {
  if (!isBrowser) return null;
  try {
    const raw = read(KEY);
    if (!raw) return null;
    const { value } = JSON.parse(raw);
    if (value === 'granted') return 'granted';
    /* 'denied' was the earlier name for the same choice */
    if (value === 'anonymous' || value === 'denied') return 'anonymous';
    return null;
  } catch {
    return null;
  }
};

const mode = () => (getConsent() === 'granted' ? 'full' : 'anonymous');

export const isOptedOut = () => isBrowser && read(OPTOUT_KEY) === '1';

/* inject analytics.js once, in the mode the visitor chose */
const inject = (attrs) => {
  if (!isBrowser || isOptedOut()) return;
  if (document.querySelector('script[data-rb-analytics]')) return;
  const s = document.createElement('script');
  s.src = attrs.src || SCRIPT;
  s.defer = true;
  s.setAttribute('data-rb-analytics', '');
  s.setAttribute('data-mode', mode());
  Object.entries(attrs)
    .filter(([k]) => k !== 'src')
    .forEach(([k, v]) => v != null && s.setAttribute(k, String(v)));
  document.head.appendChild(s);
};

/* campaign landing pages load their own, campaign-tagged copy (landing.js) */
const onLandingPage = () => isBrowser && /^\/landing\//.test(window.location.pathname);

export const loadSiteAnalytics = () => {
  if (onLandingPage()) return;
  inject({ 'data-stream': SITE_STREAM });
};

/* for landing.js: the campaign's stream, reported to the API host */
export const loadCampaignAnalytics = ({ apiBase, campaignId, streamId, title }) =>
  inject({
    'data-stream': streamId || '',
    'data-campaign': title || '',
    'data-campaignid': campaignId,
    'data-pagepath': title || '',
    'data-endpoint': `${apiBase}/api/analytics`,
  });

export const setConsent = (value) => {
  if (!isBrowser) return;
  write(KEY, JSON.stringify({ value, date: new Date().toISOString() }));
  /* switch the running script without a reload; it clears its cookie
     and session data itself when it falls back to anonymous */
  if (window.rbAnalytics && window.rbAnalytics.setMode) {
    window.rbAnalytics.setMode(value === 'granted' ? 'full' : 'anonymous');
  }
  window.dispatchEvent(new CustomEvent(CHANGE_EVENT, { detail: value }));
};

/* the objection to anonymous counting, from the privacy policy */
export const setOptOut = (optOut) => {
  if (!isBrowser) return;
  write(OPTOUT_KEY, optOut ? '1' : null);
  if (optOut) {
    document.cookie = 'rocketbatch_unique_uid=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; SameSite=Lax';
    /* a running script cannot be unloaded — a reload stops it */
    if (window.rbAnalytics) window.location.reload();
  } else {
    loadSiteAnalytics();
  }
};

/* the footer's "Privacy settings" link reopens the banner */
export const openConsent = () => {
  if (isBrowser) window.dispatchEvent(new Event(OPEN_EVENT));
};

export const CONSENT_OPEN_EVENT = OPEN_EVENT;
